import type { IHour } from "@gettactic/api";

// `since`/`until` arrive as "YYYY-MM-DDT00:00:00Z"; comparing the date part as
// text avoids the timezone shift a Date round-trip would introduce.
const toDateOnly = (value: string | null): string | null =>
  value ? value.slice(0, 10) : null;

const coversDate = (rule: IHour, date: string): boolean => {
  const since = toDateOnly(rule.since);
  const until = toDateOnly(rule.until);
  if (since && date < since) {
    return false;
  }
  if (until && date > until) {
    return false;
  }
  return true;
};

/**
 * Hour rules (holidays and custom hours) that explicitly keep the office, or
 * the given resource, open on `date` (yyyy-MM-dd). These never block a
 * reservation, so the availability endpoints stay silent about them and the
 * UI has to look them up to tell the user the office is open despite a holiday.
 */
export const findOpenHourRules = (
  rules: IHour[],
  date: string,
  resourceId: string | null
): IHour[] => {
  const seen = new Set<string>();
  const matches: IHour[] = [];
  for (const rule of rules) {
    if (rule.type === "weekday" || !rule.is_available) {
      continue;
    }
    if (rule.resource_id !== null && rule.resource_id !== resourceId) {
      continue;
    }
    if (!coversDate(rule, date) || seen.has(rule.id)) {
      continue;
    }
    seen.add(rule.id);
    matches.push(rule);
  }
  return matches;
};

/**
 * Same as `findOpenHourRules` across several selected days (yyyy-MM-dd), in
 * first-matching-day order, each rule listed once.
 */
export const findOpenHourRulesForDates = (
  rules: IHour[],
  dates: string[],
  resourceId: string | null
): IHour[] => {
  const seen = new Set<string>();
  const matches: IHour[] = [];
  for (const date of dates) {
    for (const rule of findOpenHourRules(rules, date, resourceId)) {
      if (seen.has(rule.id)) {
        continue;
      }
      seen.add(rule.id);
      matches.push(rule);
    }
  }
  return matches;
};
